import React, { useState, useEffect } from 'react';
import '../ForBrands/brandMode.css';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDependencies,
  createDependency,
  updateDependency,
  deleteDependency,
} from '../../store/actions/userActions';
import { parseISO, format } from 'date-fns';
import { calculateDaysLeft } from '../../utils/utils';
import { BASE_URL } from '../../constants';
import {
  Badge,
  BmModal,
  Btn,
  Card,
  ConfirmModal,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  MetricCard,
  Overline,
  PageHeader,
  SegmentedControl,
  TextArea,
} from '../ForBrands/ui/BrandUI';
import Icon from '../ForBrands/ui/icons';

/* ---- billing cycle: detected from the dates, never entered manually ------ */

const detectCycle = (lastPaid, expiry, cost) => {
  if (!Number(cost)) return 'free';
  if (!lastPaid || !expiry) return 'one-time';
  const days = (new Date(expiry) - new Date(lastPaid)) / 86400000;
  if (days <= 0) return 'one-time';
  if (days <= 45) return 'monthly';
  if (days <= 135) return 'quarterly';
  if (days <= 400) return 'yearly';
  return 'permanent';
};

const CYCLE_LABELS = {
  free: 'Free', 'one-time': 'One-time', monthly: 'Monthly',
  quarterly: 'Quarterly', yearly: 'Yearly', permanent: 'Permanent',
};

// Cadence is information, not a status: soft for a recurring bill, passive
// for a one-off, outline for free or permanent.
const CYCLE_TONES = {
  free: 'outline',
  'one-time': 'passive',
  monthly: 'soft',
  quarterly: 'soft',
  yearly: 'soft',
  permanent: 'outline',
};

/* ---- the services the app actually depends on ---------------------------
   Mirrors the App Health probes. Any of these missing from the tracker
   shows in the "Not tracked yet" strip with one-click add. */

const CORE_SERVICES = [
  { name: 'MongoDB Atlas', match: ['mongodb', 'mongo'] },
  { name: 'Heroku', match: ['heroku'] },
  { name: 'Google Gemini', match: ['gemini'] },
  { name: 'Cloudinary', match: ['cloudinary'] },
  { name: 'SendGrid', match: ['sendgrid'] },
  { name: 'Stripe', match: ['stripe'] },
  { name: 'Cerebro Sports', match: ['cerebro'] },
  { name: 'SportRadar', match: ['sportradar', 'sport radar'] },
  { name: 'BrightData', match: ['brightdata', 'bright data'] },
  { name: 'Genius Sports', match: ['genius'] },
  { name: 'Brandfetch', match: ['brandfetch'] },
];

const untrackedServices = (dependencies) => {
  const haystack = dependencies.map((d) => (d.name || '').toLowerCase());
  return CORE_SERVICES.filter(
    (svc) => !haystack.some((n) => svc.match.some((m) => n.includes(m))),
  );
};

/* ---- App Health tab ------------------------------------------------------
   Live probes of every external dependency (Cerebro, SportRadar, BrightData,
   SendGrid, Stripe, Cloudinary, Genius, Gemini, Mongo). Born from the
   Cerebro outage that hid for months behind silent cache fallbacks. */

// The bm status colours (tailwind.config.js bm.positive / attention /
// critical / tertiary), for Badge dots that take a colour value.
const POSITIVE = '#1f9d55';
const ATTENTION = '#edd200';
const CRITICAL = '#e2241a';

const HEALTH_STATUS = {
  ok: { tone: 'outline', dot: POSITIVE, label: 'Operational' },
  warn: { tone: 'attention', label: 'Degraded' },
  down: { tone: 'critical', label: 'Down' },
  not_configured: { tone: 'passive', label: 'Not configured' },
};

const HEALTH_DOT = {
  ok: 'bg-bm-positive',
  warn: 'bg-bm-attention',
  down: 'bg-bm-critical',
  not_configured: 'bg-bm-tertiary',
};

const OVERALL_BANNERS = {
  ok: { dot: POSITIVE, text: 'All systems operational' },
  warn: { dot: ATTENTION, text: 'Some services degraded' },
  down: { dot: CRITICAL, text: 'Service outage detected' },
};

const HealthBadge = ({ status }) => {
  const st = HEALTH_STATUS[status] || HEALTH_STATUS.down;
  return <Badge tone={st.tone} dot={st.dot}>{st.label}</Badge>;
};

// A service's breaker (the Cerebro probe reports it): while its window runs
// (`paused`) the app makes no live calls and pages serve stored data; once
// the window ends the next call tries again, so an open breaker past its
// window pauses nothing.
const shortTime = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';
};

export const breakerLine = (breaker) => {
  if (!breaker || !breaker.open) return '';
  const since = shortTime(breaker.since);
  const next = shortTime(breaker.nextProbeAt);
  const kind = String(breaker.kind || 'error').replace(/_/g, ' ');
  if (breaker.paused === false) {
    return `Live calls back on. The next one checks the feed (last failure: ${kind}${since ? `, ${since}` : ''}).`;
  }
  return `Live calls paused${since ? ` since ${since}` : ''} (${kind}).${next ? ` Next try ${next}.` : ''}`;
};

const HealthTab = () => {
  const token = useSelector((state) => state.auth.token);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedAt, setCheckedAt] = useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    fetch(`${BASE_URL}/api/users/admin/dependencies/health/`, auth)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { setData(d); setCheckedAt(new Date()); })
      .catch(() => toast.error('Could not run the health checks.'))
      .finally(() => setLoading(false));
    // App-wide usage numbers load independently so a slow probe never
    // blocks them (and vice versa).
    fetch(`${BASE_URL}/api/users/admin/dependencies/stats/`, auth)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setStats)
      .catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const services = data?.services || [];
  const serviceStats = stats?.services || {};
  const platform = stats?.platform || [];
  const downCount = services.filter((s) => s.status === 'down').length;
  const banner = OVERALL_BANNERS[data?.overall] || OVERALL_BANNERS.ok;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {data && (
          <Card padding="px-4 py-3" className="flex min-w-[260px] flex-1 items-center gap-[10px]" role="status">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: banner.dot }} />
            <span className="bm-label-l text-bm-ink">
              {banner.text}
              {downCount > 0 && `. ${downCount} service${downCount > 1 ? 's' : ''} down`}
            </span>
            {checkedAt && (
              <span className="bm-body-s text-bm-muted">Checked {checkedAt.toLocaleTimeString()}</span>
            )}
          </Card>
        )}
        <Btn variant="secondary" onClick={load} disabled={loading}>
          <Icon name="refresh" size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Checking...' : 'Run checks'}
        </Btn>
      </div>

      {/* Platform-wide numbers */}
      {platform.length > 0 && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {platform.map((p) => (
            <MetricCard key={p.label} label={p.label} value={p.value} />
          ))}
        </div>
      )}

      {loading && !data ? (
        <Loading label="Running the health checks..." />
      ) : !data ? (
        <ErrorState>Could not run the health checks. Use Run checks to try again.</ErrorState>
      ) : services.length === 0 ? (
        <EmptyState>No services reported a health check.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => {
            const usage = serviceStats[s.key] || [];
            return (
              <Card key={s.key} padding="p-4" className="flex flex-col" data-testid={`health-${s.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${HEALTH_DOT[s.status] || HEALTH_DOT.down}`} />
                    <span className="truncate bm-label-l text-bm-ink">{s.name}</span>
                  </div>
                  <HealthBadge status={s.status} />
                </div>
                <div className="mt-2 break-words bm-body-s text-bm-muted">
                  {s.detail}
                </div>
                {s.breaker?.open && (
                  <div className="mt-2 break-words bm-label-m text-bm-critical" data-testid={`breaker-${s.key}`}>
                    {breakerLine(s.breaker)}
                  </div>
                )}
                {usage.length > 0 && (
                  <div className="mt-3 space-y-1 border-t border-bm-border pt-3">
                    {usage.map((u) => (
                      <div key={u.label} className="flex items-baseline justify-between gap-3 bm-body-s">
                        <span className="text-bm-muted">{u.label}</span>
                        <span className="text-right bm-label-m text-bm-ink">{u.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-auto pt-2 bm-body-s text-bm-tertiary">{s.latencyMs} ms</div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ---- page ---------------------------------------------------------------- */

const TH = 'whitespace-nowrap border-b border-bm-border px-3 py-2.5 text-left bm-label-xs uppercase tracking-[0.06em] text-bm-muted';
const TD = 'px-3 py-2.5 bm-body-m text-bm-ink';

const Dependencies = () => {
  const dispatch = useDispatch();
  const dependencies = useSelector((state) => state.user.dependencies || []);

  const [isLoading, setIsLoading] = useState(true);
  // 'health' (live app health, default) | 'vendors' (subscription tracker)
  const [activeTab, setActiveTab] = useState('health');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDependency, setEditingDependency] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    lastPaidDate: '',
    subscriptionExpiry: '',
    cost: '',
    notes: '',
  });

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchDependencies());
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dispatch]);

  const resetForm = () => {
    setFormData({ name: '', lastPaidDate: '', subscriptionExpiry: '', cost: '', notes: '' });
    setEditingDependency(null);
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // Billing cycle is derived from the dates, live, as the admin types.
  const detectedCycle = detectCycle(formData.lastPaidDate, formData.subscriptionExpiry, formData.cost);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, billingCycle: detectedCycle };
      if (editingDependency) {
        await dispatch(updateDependency({ _id: editingDependency._id, ...payload }));
        toast.success('Dependency updated');
      } else {
        await dispatch(createDependency(payload));
        toast.success('Dependency added');
      }
      setIsDialogOpen(false);
      resetForm();
      dispatch(fetchDependencies());
    } catch (error) {
      toast.error(editingDependency ? 'Failed to update dependency' : 'Failed to add dependency');
      console.error('Error saving dependency:', error);
    }
  };

  const handleEdit = (dependency) => {
    setEditingDependency(dependency);
    setFormData({
      name: dependency.name || '',
      lastPaidDate: dependency.lastPaidDate || '',
      subscriptionExpiry: dependency.subscriptionExpiry || '',
      cost: dependency.cost !== undefined && dependency.cost !== null ? String(dependency.cost) : '',
      notes: dependency.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleQuickAdd = (name) => {
    resetForm();
    setFormData((prev) => ({ ...prev, name }));
    setIsDialogOpen(true);
  };

  // Runs once the admin confirms in the ConfirmModal.
  const handleDelete = async (_id) => {
    setDeleting(true);
    try {
      await dispatch(deleteDependency(_id));
      toast.success('Dependency deleted');
      dispatch(fetchDependencies());
    } catch (error) {
      toast.error('Failed to delete dependency');
      console.error('Error deleting dependency:', error);
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const totalCount = dependencies.length;
  const totalCost = dependencies.reduce((sum, dep) => sum + Number(dep.cost || 0), 0);
  const untracked = untrackedServices(dependencies);

  const header = (
    <PageHeader
      eyebrow="Admin Mode"
      title="Dependencies"
      subtitle="Live health of every external service, and the billing tracker behind them."
      actions={activeTab === 'vendors' && !isLoading ? (
        <Btn onClick={() => { resetForm(); setIsDialogOpen(true); }}>
          <Icon name="plus" size={14} className="mr-1.5" /> Add service
        </Btn>
      ) : null}
    />
  );

  if (isLoading) {
    return (
      <div className="w-full min-w-0 max-w-full pb-8">
        {header}
        <Loading label="Loading dependencies..." />
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-full pb-8">
      {header}

      <div className="mb-6">
        <SegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { value: 'health', label: 'App health' },
            { value: 'vendors', label: 'Vendors and billing' },
          ]}
          capitalize={false}
        />
      </div>

      {activeTab === 'health' && <HealthTab />}

      {activeTab === 'vendors' && (<>

      {/* Untracked core services: the stack the app really runs on */}
      {untracked.length > 0 && (
        <Card padding="px-5 py-4" className="mb-5">
          <Overline brand>Not tracked yet</Overline>
          <div className="mt-3 flex flex-wrap gap-2">
            {untracked.map((svc) => (
              <Btn
                key={svc.name}
                variant="secondary"
                size="sm"
                onClick={() => handleQuickAdd(svc.name)}
                aria-label={`Track ${svc.name}`}
              >
                <Icon name="plus" size={12} className="mr-1" /> {svc.name}
              </Btn>
            ))}
          </div>
        </Card>
      )}

      {/* Add / edit service (conditionally mounted) */}
      {isDialogOpen && (
        <BmModal
          title={editingDependency ? 'Edit service' : 'New service'}
          onClose={() => setIsDialogOpen(false)}
          width={560}
          footer={(
            <>
              <Btn variant="secondary" onClick={() => setIsDialogOpen(false)}>Cancel</Btn>
              <Btn type="submit" form="dependency-form">
                {editingDependency ? 'Update service' : 'Add service'}
              </Btn>
            </>
          )}
        >
          <p className="mb-5 bm-body-s text-bm-muted">
            The billing cycle is detected from the payment dates.
          </p>
          <form id="dependency-form" onSubmit={handleSubmit} className="space-y-4">
            <Field label="Service name">
              <Input
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Last paid">
                <Input
                  type="date"
                  value={formData.lastPaidDate || ''}
                  onChange={(e) => handleChange('lastPaidDate', e.target.value)}
                />
              </Field>
              <Field label="Expires">
                <Input
                  type="date"
                  value={formData.subscriptionExpiry || ''}
                  onChange={(e) => handleChange('subscriptionExpiry', e.target.value)}
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 items-end gap-3">
              <Field label="Cost (USD)">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cost || ''}
                  onChange={(e) => handleChange('cost', e.target.value)}
                />
              </Field>
              <div className="flex flex-col gap-[6px]">
                <span className="bm-label-l text-bm-ink">Detected cycle</span>
                <span className="flex h-10 items-center">
                  <Badge tone={CYCLE_TONES[detectedCycle]} size="m">{CYCLE_LABELS[detectedCycle]}</Badge>
                </span>
              </div>
            </div>
            <Field label="Notes">
              <TextArea
                rows={2}
                value={formData.notes || ''}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </Field>
          </form>
        </BmModal>
      )}

      {pendingDelete && (
        <ConfirmModal
          title="Delete service"
          body={`Delete ${pendingDelete.name || 'this service'} from the billing tracker? Its payment dates and notes are removed.`}
          confirmLabel="Delete service"
          tone="danger"
          busy={deleting}
          onConfirm={() => handleDelete(pendingDelete._id)}
          onClose={() => !deleting && setPendingDelete(null)}
        />
      )}

      {/* Table of Dependencies */}
      {dependencies.length > 0 ? (
        <>
          <div className="max-h-[calc(100vh-320px)] overflow-auto rounded-[12px] border border-bm-border bg-white">
            <table className="w-full text-left">
              <thead className="sticky top-0 z-10 bg-bm-subtle">
                <tr>
                  {['#', 'Service', 'Status', 'Last paid', 'Expires', 'Days left', 'Cost', 'Billing'].map((h) => (
                    <th key={h} className={TH}>{h}</th>
                  ))}
                  <th className={`${TH} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...dependencies]
                  .sort((a, b) => new Date(a.subscriptionExpiry) - new Date(b.subscriptionExpiry))
                  .map((dep, index) => {
                    const daysLeft = dep.subscriptionExpiry ? calculateDaysLeft(dep.subscriptionExpiry) : null;
                    const active = daysLeft !== null && daysLeft > 0;
                    const cycle = detectCycle(dep.lastPaidDate, dep.subscriptionExpiry, dep.cost);
                    return (
                      <tr key={dep._id} className="border-b border-bm-border bg-white transition-colors last:border-0 hover:bg-bm-subtle">
                        <td className={`${TD} text-bm-tertiary`}>{index + 1}</td>
                        <td className={`${TD} bm-label-l capitalize`}>{dep.name}</td>
                        <td className={TD}>
                          {active
                            ? <Badge tone="outline" dot={POSITIVE}>Active</Badge>
                            : <Badge tone="critical">Expired</Badge>}
                        </td>
                        <td className={`${TD} whitespace-nowrap text-bm-muted`}>{dep.lastPaidDate ? format(parseISO(dep.lastPaidDate), 'yyyy-MM-dd') : '-'}</td>
                        <td className={`${TD} whitespace-nowrap text-bm-muted`}>{dep.subscriptionExpiry ? format(parseISO(dep.subscriptionExpiry), 'yyyy-MM-dd') : '-'}</td>
                        <td className={`${TD} whitespace-nowrap ${active ? 'text-bm-muted' : 'bm-label-l text-bm-critical'}`}>
                          {daysLeft !== null ? (daysLeft > 0 ? daysLeft : `${Math.abs(daysLeft)} overdue`) : '-'}
                        </td>
                        <td className={TD}>${Number(dep.cost || 0).toFixed(2)}</td>
                        <td className={TD}>
                          <Badge tone={CYCLE_TONES[cycle]}>{CYCLE_LABELS[cycle]}</Badge>
                        </td>
                        <td className={TD}>
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => handleEdit(dep)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-bm-muted transition-colors hover:bg-bm-mutedbg hover:text-bm-ink"
                              title="Edit"
                              aria-label={`Edit ${dep.name}`}
                            >
                              <Icon name="edit" size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingDelete(dep)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-bm-muted transition-colors hover:bg-bm-mutedbg hover:text-bm-ink"
                              title="Delete"
                              aria-label={`Delete ${dep.name}`}
                            >
                              <Icon name="trash" size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-end gap-6 bm-label-l text-bm-muted">
            <div>Total services: {totalCount}</div>
            <div>Total cost: ${totalCost.toFixed(2)}</div>
          </div>
        </>
      ) : (
        <EmptyState>No services tracked yet. Add the core stack from the list above.</EmptyState>
      )}
      </>)}
    </div>
  );
};

export default Dependencies;
